import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useCustomer } from './customerCtx';

/**
 * AI Sales Agent — outbound campaign console.
 *
 * A tenant admin runs as many campaigns at once as their seat count allows. Each one
 * is a self-contained template: who the agent is, why she is calling, the number she
 * presents, when she is allowed to dial, what she needs to find out, and the list of
 * people to ring.
 *
 * Two safety rails are deliberately visible rather than hidden in the backend:
 *   - A new campaign is created in DRY RUN, which plans calls and dials nobody. Going
 *     live is a separate, explicit action.
 *   - Taking a RUNNING campaign out of dry run requires typing GO LIVE, because that
 *     is the moment real phones start ringing.
 */

interface Campaign {
  id: number;
  tenant_id: number;
  name: string;
  description: string | null;
  status: string;
  dry_run: boolean;
  voice_bot_id: string | null;
  agent_name: string | null;
  trunk_name: string | null;
  caller_id: string | null;
  call_purpose: string | null;
  opening_line: string | null;
  key_facts: string | null;
  engine: string | null;
  max_concurrent: number;
  max_attempts: number;
  retry_minutes: number;
  call_window_start: string;
  call_window_end: string;
  call_days: string;
  timezone: string;
  qualification: { question: string }[] | null;
  leads: number;
  qualified: number;
  remaining: number;
  attempts: number;
}

interface Lead {
  id: number; full_name: string | null; company: string | null;
  phone_e164: string; language: string | null; status: string;
  attempts: number; score: number | null; summary: string | null;
}

interface Attempt {
  id: number; full_name: string | null; phone_e164: string | null;
  placed_at: string; disposition: string | null; talk_seconds: number | null;
  turns: number | null; score: number | null; summary: string | null;
}

interface TeamMember {
  id: number; name: string; phone: string; role: string | null;
  is_active: boolean; priority: number; last_handoff_at: string | null;
}

interface Handoff {
  id: number; kind: string; status: string; created_at: string;
  full_name: string | null; phone_e164: string | null;
  contact_name: string | null; question: string | null; requested_time: string | null;
}

interface Options {
  voice_bots: { id: string; bot_name: string; voice_name: string | null; language: string }[];
  trunks: { trunk_name: string; display_name: string | null }[];
  timezones: string[];
}

interface Status {
  tenant_id: number | null; licensed: boolean; is_super_admin: boolean;
  max_concurrent_allowed: number; kill_switch: boolean;
}

const DAYS = [
  { n: '1', label: 'Mon' }, { n: '2', label: 'Tue' }, { n: '3', label: 'Wed' },
  { n: '4', label: 'Thu' }, { n: '5', label: 'Fri' }, { n: '6', label: 'Sat' },
  { n: '7', label: 'Sun' },
];

const wrap: React.CSSProperties = { padding: 24, maxWidth: 1120 };
const card: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, background: '#fff' };
const btn: React.CSSProperties = { padding: '10px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { padding: '8px 14px', background: '#fff', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
const btnDanger: React.CSSProperties = { padding: '8px 14px', background: '#fff', color: '#b00', border: '1px solid #e99', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
const input: React.CSSProperties = { padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
const label: React.CSSProperties = { display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#374151' };
const hint: React.CSSProperties = { color: '#6b7280', fontSize: 12, marginTop: 4 };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280', fontWeight: 600 };
const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 14 };

function tone(status: string, dry: boolean): React.CSSProperties {
  const base: React.CSSProperties = { padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, display: 'inline-block' };
  if (dry) return { ...base, background: '#fef3c7', color: '#92400e' };
  if (status === 'running') return { ...base, background: '#dcfce7', color: '#166534' };
  if (status === 'paused') return { ...base, background: '#e5e7eb', color: '#374151' };
  if (status === 'completed') return { ...base, background: '#dbeafe', color: '#1e40af' };
  return { ...base, background: '#f3f4f6', color: '#6b7280' };
}

export default function SalesAgentPage(_props: { user?: any }) {
  const [st, setSt] = useState<Status | null>(null);
  const [rows, setRows] = useState<Campaign[]>([]);
  const cust = useCustomer();   // "Managing customer": only the selected customer's campaigns
  const [opts, setOpts] = useState<Options | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [editId, setEditId] = useState<number | 'new' | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // campaign form
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [opening, setOpening] = useState('');
  const [botId, setBotId] = useState('');
  const [trunk, setTrunk] = useState('');
  const [cli, setCli] = useState('');
  const [conc, setConc] = useState('2');
  const [tries, setTries] = useState('3');
  const [retry, setRetry] = useState('240');
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('18:00');
  const [days, setDays] = useState<string[]>(['1', '2', '3', '4', '5']);
  const [tz, setTz] = useState('Asia/Dubai');
  const [asks, setAsks] = useState<string[]>(['']);
  const [facts, setFacts] = useState('');
  const [engine, setEngine] = useState('stream');
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [tName, setTName] = useState('');
  const [tPhone, setTPhone] = useState('');
  const [tRole, setTRole] = useState('');
  const [handoffs, setHandoffs] = useState<Handoff[]>([]);

  // leads / results for the open campaign
  const [leads, setLeads] = useState<Lead[]>([]);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [detailTab, setDetailTab] = useState<'leads' | 'calls' | 'handoffs'>('leads');
  const [lName, setLName] = useState('');
  const [lPhone, setLPhone] = useState('');
  const [lCompany, setLCompany] = useState('');
  const [lLang, setLLang] = useState('en');
  const [csv, setCsv] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [s, c] = await Promise.all([
        axios.get('/api/sales-agent/status'),
        axios.get('/api/sales-agent/campaigns'),
      ]);
      setSt(s.data);
      setRows(Array.isArray(c.data) ? c.data : []);
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Could not load campaigns');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Voice agents and permitted trunks, served by this module so a tenant admin
  // gets them without needing the wider trunk-administration privilege.
  useEffect(() => {
    axios.get('/api/sales-agent/options')
      .then(r => setOpts(r.data))
      .catch(() => setOpts(null));
  }, []);

  const loadDetail = useCallback(async (id: number) => {
    try {
      const [l, a] = await Promise.all([
        axios.get(`/api/sales-agent/campaigns/${id}/leads?limit=500`),
        axios.get(`/api/sales-agent/campaigns/${id}/attempts?limit=200`),
        axios.get(`/api/sales-agent/campaigns/${id}/handoffs`).then(r => { setHandoffs(r.data || []); return r; })
          .catch(() => { setHandoffs([]); return null; }),
      ]);
      setLeads(Array.isArray(l.data) ? l.data : []);
      setAttempts(Array.isArray(a.data) ? a.data : []);
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not load campaign detail'); }
  }, []);

  useEffect(() => { if (openId) loadDetail(openId); }, [openId, loadDetail]);

  const openEditor = (c: Campaign | null) => {
    setErr(''); setMsg('');
    if (c) {
      setEditId(c.id);
      setName(c.name || ''); setPurpose(c.call_purpose || ''); setOpening(c.opening_line || '');
      setBotId(c.voice_bot_id || ''); setTrunk(c.trunk_name || ''); setCli(c.caller_id || '');
      setConc(String(c.max_concurrent ?? 2)); setTries(String(c.max_attempts ?? 3));
      setRetry(String(c.retry_minutes ?? 240));
      setFrom((c.call_window_start || '09:00').slice(0, 5));
      setTo((c.call_window_end || '18:00').slice(0, 5));
      setDays((c.call_days || '1,2,3,4,5').split(',').filter(Boolean));
      setTz(c.timezone || 'Asia/Dubai');
      setAsks((c.qualification || []).map(q => q.question).filter(Boolean).concat(['']));
      setFacts(c.key_facts || ''); setEngine(c.engine || 'agi');
    } else {
      setEditId('new');
      setName(''); setPurpose(''); setOpening('Good day {{first_name}}, this is ');
      setBotId(''); setTrunk(''); setCli('');
      setConc('2'); setTries('3'); setRetry('240');
      setFrom('09:00'); setTo('18:00'); setDays(['1', '2', '3', '4', '5']);
      setTz('Asia/Dubai'); setAsks(['']); setFacts(''); setEngine('stream');
    }
  };

  const save = async () => {
    if (!name.trim()) { setErr('Give the campaign a name'); return; }
    setSaving(true); setErr(''); setMsg('');
    const body: any = {
      name: name.trim(),
      call_purpose: purpose.trim() || null,
      opening_line: opening.trim() || null,
      voice_bot_id: botId || null,
      trunk_name: trunk || null,
      caller_id: cli.trim() || null,
      max_concurrent: Number(conc) || 1,
      max_attempts: Number(tries) || 1,
      retry_minutes: Number(retry) || 240,
      call_window_start: from,
      call_window_end: to,
      call_days: days.slice().sort().join(',') || '1,2,3,4,5',
      timezone: tz,
      qualification: asks.map(a => a.trim()).filter(Boolean).map(q => ({ question: q })),
      key_facts: facts.trim() || null,
      engine,
    };
    try {
      if (editId === 'new') {
        const r = await axios.post('/api/sales-agent/campaigns', cust.customerId ? { ...body, customer_id: cust.customerId } : body);
        setMsg(`Campaign created in dry run. It will plan calls and dial nobody until you take it live.`);
        setEditId(null); await load(); setOpenId(r.data?.id || null);
      } else {
        await axios.put(`/api/sales-agent/campaigns/${editId}`, body);
        setMsg('Campaign saved.');
        setEditId(null); await load();
      }
    } catch (e: any) { setErr(e?.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const loadTeam = useCallback(async () => {
    try { const r = await axios.get('/api/sales-agent/team'); setTeam(r.data || []); }
    catch { setTeam([]); }
  }, []);
  useEffect(() => { if (st?.licensed) loadTeam(); }, [st, loadTeam]);

  const addTeam = async () => {
    setErr(''); setMsg('');
    try {
      await axios.post('/api/sales-agent/team', { name: tName, phone: tPhone, role: tRole || null });
      setTName(''); setTPhone(''); setTRole('');
      setMsg('Added to the senior sales team.'); await loadTeam();
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not add team member'); }
  };
  const toggleTeam = async (m: TeamMember) => {
    try { await axios.put(`/api/sales-agent/team/${m.id}`, { ...m, is_active: !m.is_active }); await loadTeam(); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Could not update team member'); }
  };
  const removeTeam = async (m: TeamMember) => {
    try { await axios.delete(`/api/sales-agent/team/${m.id}`); await loadTeam(); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Could not remove team member'); }
  };
  const setCallback = async (h: Handoff, status: string) => {
    try {
      await axios.put(`/api/sales-agent/handoffs/${h.id}`, { status });
      if (openId) await loadDetail(openId);
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not update call back'); }
  };

  const setStatus = async (c: Campaign, status: string) => {
    setErr(''); setMsg('');
    try {
      await axios.post(`/api/sales-agent/campaigns/${c.id}/status`, { status });
      setMsg(`“${c.name}” is now ${status}.`);
      await load();
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not change status'); }
  };

  const goLive = async (c: Campaign) => {
    const warn = `Take “${c.name}” out of dry run?\n\n`
      + `Real calls will be placed to ${c.remaining} number(s) from ${c.caller_id || 'the default caller ID'}, `
      + `between ${(c.call_window_start || '').slice(0, 5)} and ${(c.call_window_end || '').slice(0, 5)} ${c.timezone}.\n\n`
      + `Type GO LIVE to confirm.`;
    const typed = window.prompt(warn, '');
    if (typed === null) return;
    if (typed.trim().toUpperCase() !== 'GO LIVE') { setErr('Not confirmed — nothing changed.'); return; }
    setErr(''); setMsg('');
    try {
      await axios.put(`/api/sales-agent/campaigns/${c.id}`, { dry_run: false, confirm: 'GO LIVE' });
      setMsg(`“${c.name}” is live. Calls will be placed inside its calling window.`);
      await load();
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not go live'); }
  };

  const backToDry = async (c: Campaign) => {
    setErr(''); setMsg('');
    try {
      await axios.put(`/api/sales-agent/campaigns/${c.id}`, { dry_run: true });
      setMsg(`“${c.name}” is back in dry run. No further calls will be placed.`);
      await load();
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not change dry run'); }
  };

  const addLead = async () => {
    if (!openId) return;
    if (!lPhone.trim()) { setErr('A phone number is required'); return; }
    setErr(''); setMsg('');
    try {
      const r = await axios.post(`/api/sales-agent/campaigns/${openId}/leads`, {
        leads: [{ name: lName.trim(), phone: lPhone.trim(), company: lCompany.trim(), language: lLang }],
      });
      const d = r.data || {};
      if (d.added) setMsg(`Added ${lName.trim() || lPhone.trim()}.`);
      else if (d.skipped_duplicates) setErr('That number is already on this campaign.');
      else if (d.invalid) setErr('That number could not be read as a phone number.');
      setLName(''); setLPhone(''); setLCompany('');
      await loadDetail(openId); await load();
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not add that person'); }
  };

  const importCsv = async () => {
    if (!openId || !csv.trim()) return;
    setErr(''); setMsg('');
    try {
      const r = await axios.post(`/api/sales-agent/campaigns/${openId}/leads`, { csv });
      const d = r.data || {};
      setMsg(`Imported ${d.added || 0}. Skipped ${d.skipped_duplicates || 0} already present, `
        + `${d.invalid || 0} without a readable number.`);
      setCsv('');
      await loadDetail(openId); await load();
    } catch (e: any) { setErr(e?.response?.data?.error || 'Import failed'); }
  };

  const suppress = async (l: Lead) => {
    if (!window.confirm(`Add ${l.phone_e164} to the do-not-call list?\n\n`
      + `This stops every campaign in this tenant from calling it, now and in future.`)) return;
    setErr(''); setMsg('');
    try {
      await axios.post('/api/sales-agent/suppression', { numbers: l.phone_e164, reason: 'Do not call' });
      setMsg(`${l.phone_e164} will not be called again.`);
      if (openId) await loadDetail(openId);
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not suppress that number'); }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;

  if (st && !st.licensed) {
    return (
      <div style={wrap}>
        <h2 style={{ margin: '0 0 4px' }}>AI Sales Agent</h2>
        <div style={{ ...card, marginTop: 16 }}>
          <p style={{ margin: 0 }}>
            The AI Sales Agent module is not enabled for this account. Ask your provider to
            switch it on and set how many calls may run at once.
          </p>
        </div>
      </div>
    );
  }

  const open = openId ? rows.find(r => r.id === openId) || null : null;
  const shown = rows.filter(r => cust.match('sales_campaign', r.id));

  return (
    <div style={wrap}>
      <h2 style={{ margin: '0 0 4px' }}>AI Sales Agent</h2>
      <p style={{ color: '#666', marginTop: 0, maxWidth: 760 }}>
        Outbound campaigns run by a voice agent. A campaign holds the reason for the call,
        the people to ring, the number you present and the hours you are allowed to dial.
        You can run several at once.
      </p>

      {st?.kill_switch && (
        <div style={{ background: '#fdecea', color: '#b00', padding: 10, borderRadius: 8, marginBottom: 10 }}>
          Outbound dialling is currently stopped platform-wide by an administrator. Campaigns
          stay configured but place no calls.
        </div>
      )}
      {err && <div style={{ background: '#fdecea', color: '#b00', padding: 10, borderRadius: 8, marginBottom: 10 }}>{err}</div>}
      {msg && <div style={{ background: '#e8f5e9', color: '#1b5e20', padding: 10, borderRadius: 8, marginBottom: 10 }}>{msg}</div>}

      {editId === null && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              {shown.length} campaign{shown.length === 1 ? '' : 's'}
              {st ? ` · up to ${st.max_concurrent_allowed} calls at once per campaign` : ''}
            </div>
            <button style={btn} onClick={() => openEditor(null)}>New campaign</button>
          </div>

          <div style={{ ...card, marginBottom: 16 }}>
            <strong style={{ fontSize: 15 }}>Senior sales team</strong>
            <p style={{ ...hint, marginTop: 4 }}>
              When a customer asks something the agent cannot answer, she offers to bring in one of
              these people and, if the customer agrees, calls them and connects the customer. The
              person hears a short briefing first. If nobody picks up, she arranges a call back.
              Calls go to the active person with the lowest priority number, rotating between equals.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr auto', gap: 8, alignItems: 'end', margin: '10px 0' }}>
              <div><span style={label}>Name</span>
                <input style={input} value={tName} onChange={e => setTName(e.target.value)} placeholder="Sara Khan" /></div>
              <div><span style={label}>Mobile</span>
                <input style={input} value={tPhone} onChange={e => setTPhone(e.target.value)} placeholder="0501234567" /></div>
              <div><span style={label}>Role</span>
                <input style={input} value={tRole} onChange={e => setTRole(e.target.value)} placeholder="Senior sales consultant" /></div>
              <button style={btn} onClick={addTeam}>Add</button>
            </div>
            {team.length === 0
              ? <div style={{ ...hint, color: '#92400e' }}>
                  Nobody added yet, so the agent can only offer a call back, not connect the customer now.
                </div>
              : <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>Name</th><th style={th}>Mobile</th><th style={th}>Role</th>
                    <th style={th}>Last brought in</th><th style={th}>Status</th><th style={th}></th></tr></thead>
                  <tbody>
                    {team.map(m => (
                      <tr key={m.id}>
                        <td style={td}>{m.name}</td><td style={td}>{m.phone}</td><td style={td}>{m.role || '—'}</td>
                        <td style={td}>{m.last_handoff_at ? new Date(m.last_handoff_at).toLocaleString() : '—'}</td>
                        <td style={td}>{m.is_active ? 'active' : 'paused'}</td>
                        <td style={td}>
                          <button style={{ ...btnGhost, padding: '4px 10px' }} onClick={() => toggleTeam(m)}>
                            {m.is_active ? 'Pause' : 'Activate'}</button>{' '}
                          <button style={{ ...btnDanger, padding: '4px 10px' }} onClick={() => removeTeam(m)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </div>

          {shown.length === 0 && (
            <div style={card}>
              <p style={{ margin: 0, color: '#6b7280' }}>
                No campaigns yet. Create one, add the people to call, then take it live when
                you are happy with it.
              </p>
            </div>
          )}

          {shown.map(c => (
            <div key={c.id} style={{ ...card, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 280, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 16 }}>{c.name}</strong>
                    <span style={tone(c.status, c.dry_run)}>
                      {c.dry_run ? 'dry run' : c.status}
                    </span>
                    {!c.dry_run && c.status === 'running' && (
                      <span style={{ fontSize: 12, color: '#166534' }}>calling real numbers</span>
                    )}
                  </div>
                  {c.call_purpose && (
                    <div style={{ color: '#374151', fontSize: 13, marginTop: 6 }}>{c.call_purpose}</div>
                  )}
                  <div style={{ ...hint, marginTop: 8 }}>
                    {c.agent_name || 'no voice agent'} · presents {c.caller_id || 'default caller ID'} ·
                    {' '}{(c.call_window_start || '').slice(0, 5)}–{(c.call_window_end || '').slice(0, 5)} {c.timezone} ·
                    {' '}{(c.call_days || '').split(',').map(n => (DAYS.find(d => d.n === n) || { label: n }).label).join(' ')}
                  </div>
                  {(c as any).uae_trunk && (
                    <div style={{ ...hint, marginTop: 4, color: '#9a3412' }}>
                      UAE numbers (test): via {(c as any).uae_trunk === 'audiocodes' ? 'Dubai gateway' : (c as any).uae_trunk}
                      {(c as any).uae_caller_id ? `, presents ${(c as any).uae_caller_id}` : ''}
                      {(c as any).uae_any_time ? ', any day and time' : ''}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 13 }}>
                    <span><strong>{c.leads}</strong> people</span>
                    <span><strong>{c.remaining}</strong> still to call</span>
                    <span><strong>{c.attempts}</strong> calls made</span>
                    <span><strong>{c.qualified}</strong> qualified</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <button style={btnGhost} onClick={() => setOpenId(openId === c.id ? null : c.id)}>
                    {openId === c.id ? 'Hide people' : 'People & calls'}
                  </button>
                  <button style={btnGhost} onClick={() => openEditor(c)}>Edit</button>
                  {c.status !== 'running'
                    ? <button style={btnGhost} onClick={() => setStatus(c, 'running')}>Start</button>
                    : <button style={btnGhost} onClick={() => setStatus(c, 'paused')}>Pause</button>}
                  {c.dry_run
                    ? <button style={btn} onClick={() => goLive(c)}>Go live</button>
                    : <button style={btnDanger} onClick={() => backToDry(c)}>Back to dry run</button>}
                </div>
              </div>

              {openId === c.id && (
                <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 14 }}>
                  <div style={{ marginBottom: 12 }}>
                    <button style={{ ...btnGhost, borderColor: detailTab === 'leads' ? '#6366f1' : '#ddd', color: detailTab === 'leads' ? '#6366f1' : '#555' }}
                      onClick={() => setDetailTab('leads')}>People ({leads.length})</button>
                    <button style={{ ...btnGhost, marginLeft: 8, borderColor: detailTab === 'calls' ? '#6366f1' : '#ddd', color: detailTab === 'calls' ? '#6366f1' : '#555' }}
                      onClick={() => setDetailTab('calls')}>Calls ({attempts.length})</button>
                    <button style={{ ...btnGhost, marginLeft: 8, borderColor: detailTab === 'handoffs' ? '#6366f1' : '#ddd', color: detailTab === 'handoffs' ? '#6366f1' : '#555' }}
                      onClick={() => setDetailTab('handoffs')}>Hand-offs &amp; call backs ({handoffs.length})</button>
                  </div>

                  {detailTab === 'leads' && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1.2fr 0.7fr auto', gap: 8, alignItems: 'end', marginBottom: 14 }}>
                        <div><span style={label}>Name</span>
                          <input style={input} value={lName} onChange={e => setLName(e.target.value)} placeholder="Ahmed Hassan" /></div>
                        <div><span style={label}>Number to call</span>
                          <input style={input} value={lPhone} onChange={e => setLPhone(e.target.value)} placeholder="0501234567" /></div>
                        <div><span style={label}>Company</span>
                          <input style={input} value={lCompany} onChange={e => setLCompany(e.target.value)} placeholder="optional" /></div>
                        <div><span style={label}>Language</span>
                          <select style={input} value={lLang} onChange={e => setLLang(e.target.value)}>
                            <option value="en">English</option>
                            <option value="ar">Arabic</option>
                            <option value="ru">Russian</option>
                            <option value="hi">Hindi</option>
                            <option value="multi">Detect</option>
                          </select></div>
                        <button style={btn} onClick={addLead}>Add</button>
                      </div>

                      <details style={{ marginBottom: 14 }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#6366f1' }}>
                          Paste a list instead
                        </summary>
                        <p style={hint}>
                          First line names the columns. Recognised: name, phone, company, language.
                          Anything else is kept against the person and can be used in the opening line.
                        </p>
                        <textarea style={{ ...input, minHeight: 90, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                          value={csv} onChange={e => setCsv(e.target.value)}
                          placeholder={'name,phone,company,language\nAhmed Hassan,0501234567,Gulf Invest,en\nElena Petrova,0559876543,,ru'} />
                        <button style={{ ...btnGhost, marginTop: 8 }} onClick={importCsv}>Import</button>
                      </details>

                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr>
                            <th style={th}>Name</th><th style={th}>Number</th><th style={th}>Company</th>
                            <th style={th}>Status</th><th style={th}>Tries</th><th style={th}>Score</th>
                            <th style={th}>What the agent learned</th><th style={th}></th>
                          </tr></thead>
                          <tbody>
                            {leads.length === 0 && (
                              <tr><td style={{ ...td, color: '#6b7280' }} colSpan={8}>
                                Nobody on this campaign yet.
                              </td></tr>
                            )}
                            {leads.map(l => (
                              <tr key={l.id}>
                                <td style={td}>{l.full_name || '—'}</td>
                                <td style={td}>{l.phone_e164}</td>
                                <td style={td}>{l.company || '—'}</td>
                                <td style={td}>{l.status}</td>
                                <td style={td}>{l.attempts}</td>
                                <td style={td}>{l.score ?? '—'}</td>
                                <td style={{ ...td, maxWidth: 320, color: '#4b5563' }}>{l.summary || '—'}</td>
                                <td style={td}>
                                  {l.status !== 'suppressed' && (
                                    <button style={{ ...btnDanger, padding: '4px 10px', fontSize: 12 }}
                                      onClick={() => suppress(l)}>Do not call</button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {detailTab === 'handoffs' && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                          <th style={th}>When</th><th style={th}>Customer</th><th style={th}>Type</th>
                          <th style={th}>Asked about</th><th style={th}>Call back at</th><th style={th}>Sales person</th>
                          <th style={th}>Status</th><th style={th}></th>
                        </tr></thead>
                        <tbody>
                          {handoffs.length === 0 && (
                            <tr><td style={{ ...td, color: '#6b7280' }} colSpan={8}>No hand-offs or call backs yet.</td></tr>
                          )}
                          {handoffs.map(h => (
                            <tr key={h.id}>
                              <td style={td}>{new Date(h.created_at).toLocaleString()}</td>
                              <td style={td}>{h.full_name || '—'}<div style={hint}>{h.phone_e164 || ''}</div></td>
                              <td style={td}>{h.kind === 'transfer' ? 'connected live' : 'call back'}</td>
                              <td style={{ ...td, maxWidth: 260 }}>{h.question || '—'}</td>
                              <td style={td}>{h.requested_time || '—'}</td>
                              <td style={td}>{h.contact_name || '—'}</td>
                              <td style={td}>{h.status.replace('_', ' ')}</td>
                              <td style={td}>
                                {h.kind === 'callback' && h.status === 'pending' && (
                                  <>
                                    <button style={{ ...btnGhost, padding: '4px 10px' }} onClick={() => setCallback(h, 'done')}>Done</button>{' '}
                                    <button style={{ ...btnDanger, padding: '4px 10px' }} onClick={() => setCallback(h, 'cancelled')}>Cancel</button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {detailTab === 'calls' && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                          <th style={th}>When</th><th style={th}>Who</th><th style={th}>Number</th>
                          <th style={th}>Outcome</th><th style={th}>Talk</th><th style={th}>Turns</th>
                          <th style={th}>Score</th><th style={th}>Summary</th>
                        </tr></thead>
                        <tbody>
                          {attempts.length === 0 && (
                            <tr><td style={{ ...td, color: '#6b7280' }} colSpan={8}>No calls yet.</td></tr>
                          )}
                          {attempts.map(a => (
                            <tr key={a.id}>
                              <td style={td}>{a.placed_at ? new Date(a.placed_at).toLocaleString() : '—'}</td>
                              <td style={td}>{a.full_name || '—'}</td>
                              <td style={td}>{a.phone_e164 || '—'}</td>
                              <td style={td}>{a.disposition || '—'}</td>
                              <td style={td}>{a.talk_seconds ? `${a.talk_seconds}s` : '—'}</td>
                              <td style={td}>{a.turns ?? '—'}</td>
                              <td style={td}>{a.score ?? '—'}</td>
                              <td style={{ ...td, maxWidth: 360, color: '#4b5563' }}>{a.summary || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {editId !== null && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>{editId === 'new' ? 'New campaign' : 'Edit campaign'}</h3>

          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr 1fr' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <span style={label}>Campaign name</span>
              <input style={input} value={name} onChange={e => setName(e.target.value)}
                placeholder="Eden House Za'abeel — buyer qualification" />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <span style={label}>Purpose of the call</span>
              <textarea style={{ ...input, minHeight: 72 }} value={purpose}
                onChange={e => setPurpose(e.target.value)}
                placeholder="Qualify buyers who enquired about Eden House Za'abeel, and book viewings at the sales gallery for anyone genuinely interested." />
              <p style={hint}>
                Why the agent is ringing, in your own words. She is told this on every call and
                steers the conversation back to it if it drifts.
              </p>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <span style={label}>Opening line</span>
              <textarea style={{ ...input, minHeight: 60 }} value={opening}
                onChange={e => setOpening(e.target.value)}
                placeholder="Good day {{first_name}}, this is Fatima calling from H and H about Eden House Za'abeel. Is now a convenient moment?" />
              <p style={hint}>
                The first thing said on every call. Use <code>{'{{first_name}}'}</code>,{' '}
                <code>{'{{name}}'}</code> or <code>{'{{company}}'}</code> and they are filled in per
                person. The agent will not introduce herself again after this.
              </p>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <span style={label}>Key facts</span>
              <textarea style={{ ...input, minHeight: 120 }} value={facts}
                onChange={e => setFacts(e.target.value)}
                placeholder={"Location: beside DIFC, Za'abeel\nOne bedroom from around AED 4.7 million\nHandover: December 2028\nNOT KNOWN: payment plans, unit sizes"} />
              <p style={hint}>
                The facts the agent may quote: prices, dates, location, unit types. She gives numbers,
                dates and features only from here or the knowledge base. For anything else she offers
                a senior sales person. Listing what is NOT known helps her avoid guessing.
              </p>
            </div>

            <div>
              <span style={label}>Conversation engine</span>
              <select style={input} value={engine} onChange={e => setEngine(e.target.value)}>
                <option value="stream">Live two-way (recommended)</option>
                <option value="agi">Classic turn-by-turn</option>
              </select>
              <p style={hint}>
                Live two-way lets the customer interrupt and replies faster, and is needed to bring
                in a sales person. Classic is kept as a fallback.
              </p>
            </div>

            <div>
              <span style={label}>Voice agent</span>
              <select style={input} value={botId} onChange={e => setBotId(e.target.value)}>
                <option value="">— select —</option>
                {(opts?.voice_bots || []).map(b => (
                  <option key={b.id} value={b.id}>
                    {b.bot_name}{b.voice_name ? ` (${b.voice_name})` : ''}
                  </option>
                ))}
              </select>
              <p style={hint}>
                Who makes the call. Voice agents are set up under Auto Receptionist.
                {!opts?.voice_bots?.length && ' No voice agent exists yet for this account.'}
              </p>
            </div>

            <div>
              <span style={label}>Caller ID presented</span>
              <input style={input} value={cli} onChange={e => setCli(e.target.value)} placeholder="045264995" />
              <p style={hint}>
                Must be a number you own. Calls over the Dubai gateway need the local form,
                for example 045264995 rather than +97145264995.
              </p>
            </div>

            <div>
              <span style={label}>Route</span>
              <select style={input} value={trunk} onChange={e => setTrunk(e.target.value)}>
                <option value="">— select —</option>
                {(opts?.trunks || []).map(t => (
                  <option key={t.trunk_name} value={t.trunk_name}>
                    {t.display_name || t.trunk_name}
                  </option>
                ))}
                {/* keep a saved trunk selectable even if it is no longer in the list */}
                {trunk && !(opts?.trunks || []).some(t => t.trunk_name === trunk) && (
                  <option value={trunk}>{trunk}</option>
                )}
              </select>
              <p style={hint}>
                Which route the calls go out on. Only routes assigned to this account appear.
              </p>
            </div>

            <div>
              <span style={label}>Calls at once</span>
              <input style={input} type="number" min={1} max={st?.max_concurrent_allowed || 10}
                value={conc} onChange={e => setConc(e.target.value)} />
              <p style={hint}>
                How many of these calls may be in progress together.
                {st ? ` Maximum ${st.max_concurrent_allowed}.` : ''}
              </p>
            </div>

            <div>
              <span style={label}>Attempts per person</span>
              <input style={input} type="number" min={1} max={10} value={tries}
                onChange={e => setTries(e.target.value)} />
              <p style={hint}>Give up after this many unanswered calls.</p>
            </div>

            <div>
              <span style={label}>Wait before retrying</span>
              <input style={input} type="number" min={15} value={retry}
                onChange={e => setRetry(e.target.value)} />
              <p style={hint}>Minutes between attempts to the same person.</p>
            </div>

            <div>
              <span style={label}>Calling hours</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input style={input} type="time" value={from} onChange={e => setFrom(e.target.value)} />
                <span style={{ color: '#6b7280' }}>to</span>
                <input style={input} type="time" value={to} onChange={e => setTo(e.target.value)} />
              </div>
              <p style={hint}>No call is placed outside these hours.</p>
            </div>

            <div>
              <span style={label}>Time zone</span>
              <select style={input} value={tz} onChange={e => setTz(e.target.value)}>
                {(opts?.timezones || ['Asia/Dubai', 'Asia/Riyadh', 'Asia/Karachi', 'Europe/London', 'UTC'])
                  .map(z => <option key={z} value={z}>{z}</option>)}
                {tz && !(opts?.timezones || []).includes(tz) && <option value={tz}>{tz}</option>}
              </select>
              <p style={hint}>The hours above are read in this zone.</p>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <span style={label}>Calling days</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DAYS.map(d => {
                  const on = days.includes(d.n);
                  return (
                    <button key={d.n} type="button"
                      onClick={() => setDays(on ? days.filter(x => x !== d.n) : days.concat(d.n))}
                      style={{
                        padding: '6px 14px', borderRadius: 999, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                        border: on ? '1px solid #6366f1' : '1px solid #ddd',
                        background: on ? '#6366f1' : '#fff', color: on ? '#fff' : '#555',
                      }}>{d.label}</button>
                  );
                })}
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <span style={label}>What the agent should find out</span>
              {asks.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input style={input} value={a}
                    onChange={e => {
                      const next = asks.slice(); next[i] = e.target.value;
                      if (i === asks.length - 1 && e.target.value.trim()) next.push('');
                      setAsks(next);
                    }}
                    placeholder={i === 0 ? 'What budget range are they considering?' : 'another thing to find out'} />
                  {asks.length > 1 && (
                    <button type="button" style={{ ...btnDanger, padding: '6px 12px' }}
                      onClick={() => setAsks(asks.filter((_, j) => j !== i))}>Remove</button>
                  )}
                </div>
              ))}
              <p style={hint}>
                Things to learn, not a script. The agent asks whatever follows naturally from the
                conversation, skips anything already said, and does not read these out in order.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button style={btn} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : (editId === 'new' ? 'Create in dry run' : 'Save changes')}
            </button>
            <button style={btnGhost} onClick={() => { setEditId(null); setErr(''); setMsg(''); }}>Cancel</button>
          </div>
          {editId === 'new' && (
            <p style={{ ...hint, marginTop: 12 }}>
              New campaigns start in dry run. They work out who they would call and log it,
              without dialling anyone, so you can check the setup before any phone rings.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
